import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, X, Loader2, Users, Tag, FileText, Music, BookOpen, Image as ImageIcon,
  History as HistoryIcon, Clock, Trash2,
} from 'lucide-react';
import {
  listSearchHistory, trackSearchQuery, removeSearchQuery, clearSearchHistory,
} from '../utils/history';
import PinSpeakerButton from './PinSpeakerButton';
import { getSpeakers } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyResults = {
  speakers: [], parshios: [], topics: [],
  recordings: [], pdfs: [], pictures: [],
  total: 0,
};

// Feb-2026 (user): easter-egg — if the query mentions Chabad or Lubavitch
// we surface a playful "Nice try 😊" panel instead of running a search.
const NICE_TRY_RE = /\b(chabad|lubavitch)\b/i;
const isNiceTryQuery = (q) => NICE_TRY_RE.test(String(q || ''));

const highlight = (text, q) => {
  if (!text) return '';
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[hsl(44,62%,45%)]/40 text-white rounded-sm px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
};

const initials = (name) => {
  if (!name) return '?';
  const parts = name.replace(/^(Rabbi|R'|R\.|Rav)\s+/i, '').trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
};

/**
 * Feb-2026 (user): search recordings order should be
 *   1. Grabby / intriguing titles first (short, meaningful words)
 *   2. Recordings whose speaker has a profile picture come before those
 *      that don't — even if the "grabby" title is missing.
 *   3. Unintelligible titles (pure numbers, "shmuz 143", etc.) LAST.
 * We compute a numerical score and sort ascending (lowest score = best).
 */
const scoreTitleQuality = (title) => {
  const t = String(title || '').trim();
  if (!t) return 100;
  // Pure numbers / "#43" / "part 5" — very unintelligible
  if (/^[\s#.\-_]*\d+[\s#.\-_]*$/.test(t)) return 90;
  if (/^(shmuz|shiur|part|track|recording|audio)\s*#?\s*\d+$/i.test(t)) return 80;
  // Random UUID-ish / audio ID
  if (/^[a-f0-9]{8,}$/i.test(t)) return 85;
  // Contains letters — better
  const words = t.split(/\s+/).filter((w) => /[a-zA-Z]/.test(w));
  if (words.length === 0) return 75;
  // Very short, strong titles ("Emunah", "Bitachon") — grabby
  if (words.length <= 3 && t.length <= 32 && !/\d/.test(t)) return 5;
  // Medium-length real title
  if (words.length <= 8 && t.length <= 70) return 20;
  // Long title
  return 40;
};

const sortRecordingsForSearch = (recs, speakersWithPics) => {
  if (!Array.isArray(recs)) return recs;
  const withPics = speakersWithPics || new Set();
  const scored = [...recs].sort((a, b) => {
    const scoreA = scoreTitleQuality(a.title) + (withPics.has(a.speaker) ? 0 : 15);
    const scoreB = scoreTitleQuality(b.title) + (withPics.has(b.speaker) ? 0 : 15);
    return scoreA - scoreB;
  });

  // Feb-2026 (user): collapse near-duplicate titles by the same speaker.
  // "Ameilus B'Torah 1", "Ameilus B'Torah 2" and "Ameilus B'Torah (part 3)"
  // should be represented by ONE row, not 7. We normalise the title by
  // lower-casing and stripping punctuation + trailing number/part suffix,
  // then keep only the FIRST occurrence of each (speaker, normalised) pair.
  const norm = (title) => {
    let t = String(title || '').toLowerCase();
    t = t.replace(/\(duplicate\)/g, '');
    t = t.replace(/\bpart\s*\d+\b/g, '');
    t = t.replace(/\bshiur\s*\d+\b/g, '');
    t = t.replace(/[#()\-_.,:'"“”‘’!?]/g, ' ');
    t = t.replace(/\s+\d+\s*$/g, '');
    t = t.replace(/\s+/g, ' ').trim();
    return t;
  };
  const seen = new Set();
  const deduped = [];
  for (const r of scored) {
    const key = `${(r.speaker || '').toLowerCase()}::${norm(r.title)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(r);
  }
  return deduped;
};

const buildFlat = (r) => {
  const flat = [];
  r.speakers.forEach((s) => flat.push({ kind: 'speaker', item: s }));
  r.parshios.forEach((p) => flat.push({ kind: 'parsha', item: p }));
  r.topics.forEach((t) => flat.push({ kind: 'topic', item: t }));
  r.recordings.forEach((rec) => flat.push({ kind: 'recording', item: rec }));
  r.pdfs.forEach((p) => flat.push({ kind: 'pdf', item: p }));
  r.pictures.forEach((p) => flat.push({ kind: 'picture', item: p }));
  return flat;
};

const GroupLabel = ({ icon: Icon, children, count }) => (
  <div className="px-4 pt-4 pb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-semibold text-[hsl(44,62%,60%)]">
    <Icon className="w-3 h-3" />
    <span className="flex-1">{children}</span>
    {typeof count === 'number' && <span className="text-white/40">{count}</span>}
  </div>
);

const SearchModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(emptyResults);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [searchHistory, setSearchHistory] = useState([]);
  const [speakersWithPics, setSpeakersWithPics] = useState(() => new Set());
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);
  const listRef = useRef(null);

  // Fetch the pic-having speaker set once so search results can push
  // speakers-with-pics ahead of icon-less ones.
  useEffect(() => {
    if (!open) return;
    if (speakersWithPics.size > 0) return;
    getSpeakers()
      .then((list) => {
        const s = new Set();
        (list || []).forEach((sp) => {
          if (sp && sp.image && typeof sp.image === 'string' && (sp.image.startsWith('http') || sp.image.startsWith('/'))) {
            s.add(sp.name);
          }
        });
        setSpeakersWithPics(s);
      })
      .catch(() => { /* ignore */ });
  }, [open, speakersWithPics.size]);

  // Refresh local caches when opened
  useEffect(() => {
    if (!open) return;
    setSearchHistory(listSearchHistory());
    // Autofocus — on iOS Safari the keyboard only appears if focus()
    // fires inside the same tick as the user gesture, so we call it
    // both synchronously (rAF for next paint) AND after a 30ms fallback
    // for slow Android tabs that need the modal fully painted first.
    requestAnimationFrame(() => inputRef.current?.focus());
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults(emptyResults);
      setActiveIdx(-1);
      setLoading(false);
    }
  }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setResults(emptyResults);
      setLoading(false);
      return;
    }
    // Easter egg: skip network entirely for blocked terms.
    if (isNiceTryQuery(q)) {
      setResults(emptyResults);
      setLoading(false);
      return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const mySeq = ++requestSeq.current;
      try {
        const { data } = await axios.get(`${API}/api/search/global`, {
          params: { q, limit_per_group: 6 },
        });
        if (mySeq === requestSeq.current) {
          // Feb-2026 (user): reorder recordings so grabby titles come
          // first, unintelligible ones come last, and speakers with
          // profile pictures beat icon-less ones on tiebreaks.
          const sortedData = {
            ...data,
            recordings: sortRecordingsForSearch(data.recordings || [], speakersWithPics),
          };
          setResults(sortedData);
          setActiveIdx(-1);
        }
      } catch {
        if (mySeq === requestSeq.current) setResults(emptyResults);
      } finally {
        if (mySeq === requestSeq.current) setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  // Global keyboard shortcut is handled outside; this handles Escape/close only.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Mobile: tap-outside-to-close. On mobile the modal is a floating
  // top-anchored card with a pointer-events-none wrapper, so clicks
  // fall through to the homescreen below. We still want tapping the
  // homescreen to (a) close the modal and (b) let the tap navigate
  // naturally. Adding a pointerdown listener at capture level closes
  // the modal but lets the underlying element receive the same tap.
  useEffect(() => {
    if (!open) return;
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
    if (!isMobile) return;
    const onDown = (e) => {
      const card = document.querySelector('[data-testid="search-modal"]');
      if (card && card.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, onClose]);

  // Lock body scroll while open — but ONLY on desktop. On mobile the
  // modal opens as a top-anchored dropdown card so users can still see
  // (and tap) the home screen behind it; if we lock scroll there they
  // get stuck staring at a white void when they try to swipe up.
  useEffect(() => {
    if (!open) return;
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
    if (isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const flat = useMemo(() => buildFlat(results), [results]);

  const goToRoute = useCallback((kind, item) => {
    // Save search query on any explicit navigation from typing
    if (query.trim()) trackSearchQuery(query.trim());
    onClose();
    switch (kind) {
      case 'speaker':
        // Feb-2026 (user request): clicking a speaker in search always
        // goes to that speaker's own page — NOT to a filtered library
        // listing of every recording of his. His page already shows all
        // his folders + recordings; this feels much more natural.
        navigate(`/speaker/${encodeURIComponent(item.name)}`);
        break;
      case 'parsha':
        navigate(`/parsha/${item.slug}`);
        break;
      case 'topic':
        navigate(`/topic/${item.slug}`);
        break;
      case 'recording':
        navigate(`/player/${item.drive_file_id || item.id}`);
        break;
      case 'pdf':
        navigate('/cc-publications');
        break;
      case 'picture':
        navigate('/cc-picture');
        break;
      default:
        break;
    }
  }, [navigate, onClose, query]);

  const submitFullSearch = useCallback((q) => {
    const finalQ = (q ?? query).trim();
    if (!finalQ) return;
    if (isNiceTryQuery(finalQ)) return; // easter-egg: don't navigate away
    trackSearchQuery(finalQ);
    onClose();
    navigate(`/library?search=${encodeURIComponent(finalQ)}`);
  }, [navigate, onClose, query]);

  const handleKeyDown = (e) => {
    if (!flat.length) {
      if (e.key === 'Enter') submitFullSearch();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? flat.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && flat[activeIdx]) {
        goToRoute(flat[activeIdx].kind, flat[activeIdx].item);
      } else {
        submitFullSearch();
      }
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (activeIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-flat-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  const hasQuery = query.trim().length > 0;
  const isBlocked = hasQuery && isNiceTryQuery(query.trim());
  const isEmptyResults = !loading && !isBlocked && flat.length === 0 && hasQuery;

  // Compute flat index alongside rendering
  let cursor = -1;
  const nextIdx = () => ++cursor;

  const renderRow = (idx, isActive, key, dataTestId, onClick, children) => (
    <button
      key={key}
      type="button"
      data-flat-idx={idx}
      onMouseEnter={() => setActiveIdx(idx)}
      onClick={onClick}
      data-testid={dataTestId}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
        isActive ? 'bg-white/[0.08]' : 'hover:bg-white/[0.05]'
      }`}
    >
      {children}
    </button>
  );

  const modalContent = (
    <div
      // On mobile: dropdown-style overlay anchored to the top so the
      // homescreen stays fully visible + tappable below the results
      // panel. No blocking backdrop — the user can scroll the page
      // behind and tap any home tile to navigate away (the ambient
      // click closes this modal first, then propagates naturally).
      className="fixed inset-x-0 top-0 sm:inset-0 z-[9999] flex flex-col sm:items-start sm:justify-center sm:pt-[9vh] sm:px-4 animate-in fade-in duration-150 pointer-events-none"
      data-testid="search-modal-backdrop"
    >
      {/* Backdrop — full-strength on desktop, absent on mobile so home is visible */}
      <div
        className="hidden sm:block absolute inset-0 bg-black/70 backdrop-blur-sm pointer-events-auto"
        onMouseDown={onClose}
      />
      <div
        onMouseDown={(e) => e.stopPropagation()}
        // On mobile: a top-anchored floating card. Height is bounded so
        // ~35% of the homescreen remains visible below — pull-to-close
        // works because tapping anywhere outside dismisses (pointer-
        // events on the wrapper are none, only the card grabs input).
        // On sm+: keep the centred card + rounded corners.
        className="pointer-events-auto relative z-10 w-full max-h-[65dvh] sm:max-h-none sm:h-auto sm:max-w-2xl sm:rounded-2xl sm:border sm:border-[#c9a437]/25 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden flex flex-col sm:mx-auto"
        style={{
          // Frosted logo-teal liquid glass panel — Feb-2026 (user:
          // "make the liquid glass look used every where you can").
          background: 'linear-gradient(180deg, rgba(3,39,58,0.85) 0%, rgba(4,42,61,0.96) 100%)',
          WebkitBackdropFilter: 'blur(22px) saturate(170%)',
          backdropFilter: 'blur(22px) saturate(170%)',
        }}
        data-testid="search-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* Search input */}
        <div
          className="flex items-center gap-3 px-4 py-3 border-b border-white/10"
          style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)' }}
        >
          <Search className="w-4 h-4 text-white/40 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            inputMode="search"
            enterKeyHint="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search speakers, parshios, topics, recordings…"
            className="flex-1 bg-transparent border-0 outline-none text-white placeholder:text-white/40 text-[15px]"
            data-testid="search-modal-input"
            autoComplete="off"
            spellCheck={false}
          />
          {/* Feb 2026 (user: "there is 3 xs to ex out — make there only
              1"): the previous UI had three: the browser's native
              type="search" clear, our own "Clear query" button, and a
              separate "Close" button. type is now `text` (kills the
              native X); the single button below clears the query when
              there's text and closes the modal when empty. */}
          <button
            onClick={() => {
              if (query) {
                setQuery('');
                inputRef.current?.focus();
              } else {
                onClose();
              }
            }}
            className="p-1.5 rounded-md text-white/60 hover:text-white hover:bg-white/10"
            data-testid="search-modal-close-hint"
            aria-label={query ? 'Clear' : 'Close'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — fills whatever height remains between the input and the
            footer, both on mobile (full-screen sheet) and desktop (bounded
            card with sm:max-h). `overscroll-contain` stops the parent
            document from scrolling when the user reaches the top/bottom
            of the results list (fixes the "shaky" scroll on iOS Safari
            where a rubber-band bounce would eat pointer events). */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto overscroll-contain py-2 sm:flex-none sm:max-h-[60vh]"
        >
          {loading && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-white/60">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Searching…
            </div>
          )}

          {isEmptyResults && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-white/70">No matches for &ldquo;{query}&rdquo;.</p>
              <p className="text-[11px] text-white/40 mt-1">
                Try a Rabbi&apos;s name, a parsha, or a topic like &ldquo;tefilla&rdquo;.
              </p>
            </div>
          )}

          {isBlocked && (
            <div
              data-testid="search-nice-try"
              className="px-4 py-10 text-center flex flex-col items-center gap-3"
            >
              <div className="text-5xl leading-none">😊</div>
              <p className="text-lg font-semibold text-white tracking-wide">
                Nice try
              </p>
              <p className="text-xs text-white/50 max-w-xs">
                That&apos;s not something you&apos;ll find in The Vaad Otzar.
              </p>
            </div>
          )}

          {/* Empty state: recent searches + recently viewed */}
          {!hasQuery && !loading && (
            <>
              {searchHistory.length > 0 && (
                <>
                  <div className="px-4 pt-3 pb-2 flex items-center gap-2">
                    <HistoryIcon className="w-3 h-3 text-[hsl(44,62%,60%)]" />
                    <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[hsl(44,62%,60%)] flex-1">
                      Recent searches
                    </span>
                    <button
                      onClick={() => { clearSearchHistory(); setSearchHistory([]); }}
                      className="text-[10px] text-white/40 hover:text-white/70 flex items-center gap-1"
                      data-testid="search-clear-history"
                    >
                      <Trash2 className="w-3 h-3" /> Clear
                    </button>
                  </div>
                  <div className="px-3 pb-2 flex flex-wrap gap-2">
                    {searchHistory.map((h) => (
                      <div
                        key={h.q}
                        className="group flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full pl-3 pr-1 py-1 text-xs text-white/85 transition"
                      >
                        <button
                          onClick={() => submitFullSearch(h.q)}
                          className="flex items-center gap-1.5"
                          data-testid={`search-recent-${h.q}`}
                        >
                          <Clock className="w-3 h-3 text-white/40" />
                          <span>{h.q}</span>
                        </button>
                        <button
                          onClick={() => {
                            removeSearchQuery(h.q);
                            setSearchHistory(listSearchHistory());
                          }}
                          className="ml-0.5 p-0.5 rounded-full opacity-0 group-hover:opacity-100 hover:bg-white/10 transition"
                          aria-label={`Remove ${h.q}`}
                        >
                          <X className="w-3 h-3 text-white/60" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {searchHistory.length === 0 && (
                <div className="px-6 py-10 text-center">
                  <Search className="w-8 h-8 text-white/20 mx-auto mb-3" />
                  <p className="text-sm text-white/70">Search the whole library</p>
                  <p className="text-[11px] text-white/40 mt-1">
                    Speakers, parshios, topics, recordings, PDFs, and pictures.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Live results */}
          {!loading && hasQuery && flat.length > 0 && (
            <>
              {results.speakers.length > 0 && (
                <div>
                  <GroupLabel icon={Users} count={results.speakers.length}>Speakers</GroupLabel>
                  {results.speakers.map((s) => {
                    const idx = nextIdx();
                    const isActive = idx === activeIdx;
                    return (
                      <div
                        key={`sp-${s.slug}`}
                        data-flat-idx={idx}
                        onMouseEnter={() => setActiveIdx(idx)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${
                          isActive ? 'bg-white/[0.08]' : 'hover:bg-white/[0.05]'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => goToRoute('speaker', s)}
                          data-testid={`gsearch-speaker-${s.slug}`}
                          className="flex-1 flex items-center gap-3 text-left min-w-0"
                        >
                          {s.image ? (
                            <img loading="lazy" decoding="async" src={s.image} alt={s.name} className="w-9 h-9 rounded-full object-cover ring-1 ring-white/15 shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[hsl(44,62%,37%)] to-[hsl(43,67%,25%)] flex items-center justify-center text-[11px] font-bold text-white shrink-0">
                              {initials(s.name)}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-white truncate leading-tight">
                              {highlight(s.name, query)}
                            </p>
                            <p className="text-[11px] text-white/50 mt-0.5">
                              {s.count} recording{s.count === 1 ? '' : 's'}
                            </p>
                          </div>
                        </button>
                        <PinSpeakerButton speaker={s} variant="icon" />
                      </div>
                    );
                  })}
                </div>
              )}

              {results.parshios.length > 0 && (
                <div>
                  <GroupLabel icon={FileText} count={results.parshios.length}>Parshios</GroupLabel>
                  {results.parshios.map((p) => {
                    const idx = nextIdx();
                    const isActive = idx === activeIdx;
                    return renderRow(idx, isActive, `ps-${p.slug}`, `gsearch-parsha-${p.slug}`, () => goToRoute('parsha', p), (
                      <>
                        <div className="w-9 h-9 rounded-md bg-[hsl(222,40%,20%)] flex items-center justify-center text-[hsl(44,62%,55%)] shrink-0">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-white truncate leading-tight">
                            {highlight(p.name, query)}
                          </p>
                          <p className="text-[11px] text-white/50 mt-0.5">
                            Parsha · {p.count} shiur{p.count === 1 ? '' : 'im'}
                          </p>
                        </div>
                      </>
                    ));
                  })}
                </div>
              )}

              {results.topics.length > 0 && (
                <div>
                  <GroupLabel icon={Tag} count={results.topics.length}>Topics</GroupLabel>
                  {results.topics.map((t) => {
                    const idx = nextIdx();
                    const isActive = idx === activeIdx;
                    return renderRow(idx, isActive, `tp-${t.slug}`, `gsearch-topic-${t.slug}`, () => goToRoute('topic', t), (
                      <>
                        <div className="w-9 h-9 rounded-md bg-[hsl(222,40%,20%)] flex items-center justify-center text-[hsl(44,62%,55%)] shrink-0">
                          <Tag className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-white truncate leading-tight">
                            {highlight(t.name, query)}
                          </p>
                          <p className="text-[11px] text-white/50 mt-0.5">
                            Topic · {t.count} shiur{t.count === 1 ? '' : 'im'}
                          </p>
                        </div>
                      </>
                    ));
                  })}
                </div>
              )}

              {results.recordings.length > 0 && (
                <div>
                  <GroupLabel icon={Music} count={results.recordings.length}>Shiurim</GroupLabel>
                  {results.recordings.map((r) => {
                    const idx = nextIdx();
                    const isActive = idx === activeIdx;
                    return renderRow(idx, isActive, `rc-${r.drive_file_id || r.id}`, `gsearch-recording-${r.drive_file_id || r.id}`, () => goToRoute('recording', r), (
                      <>
                        <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[hsl(222,40%,22%)] to-[hsl(222,50%,12%)] flex items-center justify-center text-[hsl(44,62%,55%)] shrink-0">
                          <Music className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-white truncate leading-tight">
                            {highlight(r.title, query)}
                          </p>
                          <p className="text-[11px] text-white/50 mt-0.5 truncate">
                            {r.speaker || 'Unknown speaker'}
                            {r.parsha ? ` · ${r.parsha}` : ''}
                          </p>
                        </div>
                      </>
                    ));
                  })}
                </div>
              )}

              {results.pdfs.length > 0 && (
                <div>
                  <GroupLabel icon={BookOpen} count={results.pdfs.length}>PDFs</GroupLabel>
                  {results.pdfs.map((p) => {
                    const idx = nextIdx();
                    const isActive = idx === activeIdx;
                    return renderRow(idx, isActive, `pd-${p.id}`, `gsearch-pdf-${p.id}`, () => goToRoute('pdf', p), (
                      <>
                        <div className="w-9 h-9 rounded-md bg-[hsl(222,40%,20%)] flex items-center justify-center text-[hsl(44,62%,55%)] shrink-0">
                          <BookOpen className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-white truncate leading-tight">
                            {highlight(p.title, query)}
                          </p>
                          <p className="text-[11px] text-white/50 mt-0.5">CC PDF Library</p>
                        </div>
                      </>
                    ));
                  })}
                </div>
              )}

              {results.pictures.length > 0 && (
                <div>
                  <GroupLabel icon={ImageIcon} count={results.pictures.length}>Pictures</GroupLabel>
                  {results.pictures.map((p) => {
                    const idx = nextIdx();
                    const isActive = idx === activeIdx;
                    return renderRow(idx, isActive, `pc-${p.id}`, `gsearch-picture-${p.id}`, () => goToRoute('picture', p), (
                      <>
                        <div className="w-9 h-9 rounded-md bg-[hsl(222,40%,20%)] flex items-center justify-center text-[hsl(44,62%,55%)] shrink-0">
                          <ImageIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-white truncate leading-tight">
                            {highlight(p.title, query)}
                          </p>
                          <p className="text-[11px] text-white/50 mt-0.5">CC Picture Library</p>
                        </div>
                      </>
                    ));
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className="border-t border-white/10 bg-white/[0.02] flex items-center gap-3 px-4 py-2 text-[11px] text-white/50"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}
        >
          {/* Keyboard hints (↑ ↓ Enter / ESC) were removed per the
              "no keyboard shortcut icons anywhere except CC Family Tree"
              rule (Feb 2026). Do NOT re-add them here. */}
          <div className="sm:hidden text-white/60 text-[10px]">Tap a result to open · tap outside to close</div>
          <div className="flex-1" />
          {hasQuery && !isBlocked && (
            <button
              onClick={() => submitFullSearch()}
              className="text-white/70 hover:text-white flex items-center gap-1.5"
              data-testid="search-modal-see-all"
            >
              See all library results
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SearchModal;
